import { RefObject, useEffect } from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';
import { _notNil } from '@/littledash';
import { DataTableRect } from '../service/DataTableSelection';
import type { DataTableService } from '../DataTable.model';

interface UseScrollSyncOptions {
  scrollElementRef: RefObject<HTMLElement>;
  horizontalTargetRef?: RefObject<HTMLElement>;
  verticalTargetRef?: RefObject<HTMLElement>;
  rowVirtualizer?: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer?: Virtualizer<HTMLDivElement, Element>;
  dataTableService?: DataTableService;
}

export const useScrollSync = ({
  scrollElementRef,
  horizontalTargetRef,
  verticalTargetRef,
  rowVirtualizer,
  columnVirtualizer,
  dataTableService,
}: UseScrollSyncOptions): void => {
  useEffect(() => {
    const scrollElement = scrollElementRef.current;

    if (_notNil(scrollElement)) {
      const abort = new AbortController();
      let rafId: number | null = null;
      let lastScrollLeft = 0;
      let lastScrollTop = 0;

      const syncScroll = () => {
        if (_notNil(horizontalTargetRef?.current)) {
          horizontalTargetRef.current.style.transform = `translateX(-${lastScrollLeft}px)`;
        }

        if (_notNil(verticalTargetRef?.current)) {
          verticalTargetRef.current.style.transform = `translateY(-${lastScrollTop}px)`;
        }

        // Notify service of visible window for data loading
        if (_notNil(rowVirtualizer) && _notNil(columnVirtualizer) && _notNil(dataTableService)) {
          const rowItems = rowVirtualizer.getVirtualItems();
          const columnItems = columnVirtualizer.getVirtualItems();
          const top = rowItems[0]?.index ?? 0;
          const bottom = rowItems[rowItems.length - 1]?.index ?? top;
          const left = columnItems[0]?.index ?? 0;
          const right = columnItems[columnItems.length - 1]?.index ?? left;
          dataTableService.windowScrollChange(new DataTableRect({ top, bottom, left, right }));
        }

        rafId = null;
      };

      scrollElement.addEventListener(
        'scroll',
        (event) => {
          const target = event.target as HTMLElement;

          lastScrollLeft = target.scrollLeft;
          lastScrollTop = target.scrollTop;

          if (rafId !== null) {
            cancelAnimationFrame(rafId);
          }

          rafId = requestAnimationFrame(syncScroll);
        },
        {
          signal: abort.signal,
          passive: true,
        }
      );

      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        abort.abort();
      };
    }
  }, [scrollElementRef, horizontalTargetRef, verticalTargetRef, rowVirtualizer, columnVirtualizer, dataTableService]);
};
